import { Box, Fade } from '@mui/material'
import cn from 'classnames'
import LottieSuccessIcon from 'ext-ui/components/Dropdown/components/LottieSuccessIcon/LottieSuccessIcon'
import styles from 'ext-ui/components/Dropdown/components/SingleDropdownHandle/SingleDropdownHandle.style'
import { DropdownHandleProps } from 'ext-ui/components/Dropdown/hooks/useDropdownLogic'
import Processing from 'ext-ui/components/Processing/Processing'
import TextInput, {
  TextInputProps,
  TextInputRef,
  TextInputVariant,
} from 'ext-ui/components/TextInput/TextInput'
import { TooltipIconProps } from 'ext-ui/components/TooltipIcon/TooltipIcon'
import {
  LoadingState,
  useLoadingStateAnimation,
} from 'ext-ui/hooks/useLoadingStateAnimation'
import {
  base_colorAction,
  base_colorAlert,
  text_colorTextPrimary,
} from 'ext-ui/style/colors'
import { transition_slower_raw } from 'ext-ui/style/transitionSpeeds'
import React, {
  forwardRef,
  HTMLInputAutoCompleteAttribute,
  useState,
} from 'react'
import SvgChevronUp from 'src/components/Svgs/SvgChevronUp'
import SvgClose from 'src/components/Svgs/SvgClose'
import { assertNever } from 'src/queries/agent/network/agent.network'

export type SingleDropdownHandleProps = DropdownHandleProps &
  Omit<TextInputProps, 'iconRight'> & {
    open: boolean
    selectedValue?: {
      value: string
      label?: string
    }
    autoComplete?: HTMLInputAutoCompleteAttribute
    onAutofillDetected?: (value: string) => void
    /**
     * will render a TooltipIcon next to the label
     */
    tooltipProps?: TooltipIconProps
    /**
     * custom tooltip element to render next to the label
     */
    tooltip?: React.ReactElement
    readOnly?: boolean
    /**
     * Loading state for success animation (idle | loading | success | error)
     */
    loadingState?: LoadingState
    searchable?: boolean
    /**
     * Callback to clear the selected value
     */
    onClear?: () => void | Promise<void>
  }

const SingleDropdownHandle = forwardRef<
  TextInputRef,
  SingleDropdownHandleProps
>((props, ref) => {
  const {
    open,
    selectedValue,
    disabled,
    variant = 'medium',
    state,
    className,
    toggleButtonProps,
    onClick,
    onKeyDown,
    autoComplete,
    onAutofillDetected,
    tooltipPosition,
    tooltip,
    tooltipProps,
    loadingState,
    searchable,
    onClear,
    ...rest
  } = props

  const [isHovered, setIsHovered] = useState(false)
  const [isFocused, setIsFocused] = useState(false)

  const { showIdle, showPending, showSuccess, showError } =
    useLoadingStateAnimation(loadingState)

  // TODO: simplify svg props after moving them into ext-ui svg folder, can just use css after
  const getChevronProps = (variant: TextInputVariant) => {
    switch (variant) {
      case 'medium':
        return {
          fill: state === 'error' ? base_colorAlert : base_colorAction,
          width: 24,
          height: 24,
        }
      case 'small':
        return {
          fill: state === 'error' ? base_colorAlert : text_colorTextPrimary,
          width: 16,
          height: 16,
          viewBox: '0 0 24 24',
        }
      default:
        assertNever(variant)
    }
  }

  const getCloseProps = (variant: TextInputVariant) => {
    switch (variant) {
      case 'medium':
        return {
          stroke: state === 'error' ? base_colorAlert : base_colorAction,
          width: 24,
          height: 24,
        }
      case 'small':
        return {
          stroke: state === 'error' ? base_colorAlert : text_colorTextPrimary,
          width: 16,
          height: 16,
          viewBox: '0 0 24 24',
        }
      default:
        assertNever(variant)
    }
  }

  const handleClearClick = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent dropdown from opening
    return onClear?.()
  }

  const hasValue = selectedValue && selectedValue.value !== ''
  const shouldShowClear = hasValue && (isHovered || isFocused || open)

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn('SingleDropdownHandle', className)}
      css={styles}
    >
      <TextInput
        ref={ref}
        value={selectedValue?.label ?? selectedValue?.value ?? ''}
        /**
         * we need to define onChange to avoid uncontrolled to controlled warning
         * and we want users to be able to type to trigger the dropdown,
         * but the actual input change is handled by the dropdown logic
         */
        onChange={(e) => {
          // autofill will directly apply its edits to the handle input, so we can process that however we like
          onAutofillDetected?.(e.target.value)
        }}
        onClick={onClick}
        onKeyDown={onKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        variant={variant}
        state={state}
        disabled={disabled || loadingState === 'pending'}
        autoComplete={autoComplete}
        tooltipPosition={tooltipPosition}
        tooltip={tooltip}
        tooltipProps={tooltipProps}
        iconRight={
          <div className="icon-container">
            <Fade in={shouldShowClear} timeout={300} unmountOnExit mountOnEnter>
              <button
                tabIndex={-1}
                type="button"
                className={cn('clear-button', variant)}
                aria-label="Clear selection"
                onClick={handleClearClick}
                data-testid="clear-selection"
                disabled={disabled || loadingState === 'pending'}
              >
                <SvgClose className="close-icon" {...getCloseProps(variant)} />
              </button>
            </Fade>
            <button
              {...toggleButtonProps}
              className={cn('dropdown-button', variant)}
              aria-label={'Open dropdown'}
              disabled={disabled || loadingState === 'pending'}
              type="button"
              data-testid="arrow"
            >
              <Fade
                in={showIdle || showError}
                timeout={transition_slower_raw}
                unmountOnExit
                mountOnEnter
              >
                <Box className="animation-icon-container">
                  <SvgChevronUp
                    className={cn('icon', { open })}
                    {...getChevronProps(variant)}
                  />
                </Box>
              </Fade>
              <Fade
                in={showPending}
                timeout={transition_slower_raw}
                unmountOnExit
                mountOnEnter
              >
                <Box className="animation-icon-container">
                  <Processing
                    // TODO: simplify svg props after moving them into ext-ui svg folder, can just use css after
                    width={variant === 'medium' ? 24 : 16}
                    height={variant === 'medium' ? 24 : 16}
                  />
                </Box>
              </Fade>
              <Fade
                in={showSuccess}
                timeout={transition_slower_raw}
                unmountOnExit
                mountOnEnter
              >
                <Box className="animation-icon-container">
                  <LottieSuccessIcon play={showSuccess} freezeOnLastFrame />
                </Box>
              </Fade>
            </button>
          </div>
        }
        {...rest}
        readOnly={!searchable}
      />
    </div>
  )
})

export default SingleDropdownHandle
